/**
 * Cart Management System - Moon Moon Agency v2.1
 * Shopify cart with AJAX updates, custom events, and UI sync
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const CART_CONFIG = {
	// API Settings
	debounceDelay: 500,
	apiTimeout: 10000,

	// Selectors
	selectors: {
		cart: '[data-cart], .mm-cart',
		cartWrapper: '[data-cart-wrapper]',
		cartProducts: '[data-cart-products]',
		cartBubbles: '[data-cart-count-bubble]',
		addToCartForm: '[data-type="add-to-cart-form"]',
		cartClose: '[data-cart-close]',
		checkoutBtn: '[data-checkout-btn]',
		cartNote: '[data-cart-note]',
		drawerCart: '[data-drawer-cart]',
		cartItemById: '[data-cart-item-id="{{id}}"]',
		lineIndex: '[data-line-index="{{index}}"]',
		itemQuantity: '[data-item-quantity]',
		freeProducts: '[data-free-products]'
	},

};

/*********************************************************************************************
 * 🎁 CADEAUX REFUSÉS (gifts déclinés par le client)
 *
 * Quand un client supprime un cadeau à ajout automatique, on mémorise son refus
 * pour la durée de la session : processFreeProducts ne le ré-ajoute alors plus
 * automatiquement, et le teaser « Ajouter » réapparaît (réajout manuel possible).
 */
const DeclinedGifts = {
	key: 'mm_declined_gifts',
	_read() {
		try { return JSON.parse(sessionStorage.getItem(this.key)) || []; }
		catch { return []; }
	},
	_write(list) {
		try { sessionStorage.setItem(this.key, JSON.stringify(list)); } catch {}
	},
	has(variantId) { return this._read().includes(String(variantId)); },
	add(variantId) {
		const list = this._read();
		if (!list.includes(String(variantId))) { list.push(String(variantId)); this._write(list); }
	},
	remove(variantId) {
		this._write(this._read().filter(v => v !== String(variantId)));
	}
};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Use global debounce from mm_utils.js

// Cart Event Emitter
const cartEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'cart'
	};
	
	return document.dispatchEvent(new CustomEvent(`cart:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache
const DOM = {
	get cart() { return document.querySelector(CART_CONFIG.selectors.cart); },
	get cartSection() { return document.getElementById(this.cart?.id || ''); },
	get cartProducts() { return this.cartSection?.querySelector(CART_CONFIG.selectors.cartProducts); },
	get cartWrapper() { return document.querySelector(CART_CONFIG.selectors.cartWrapper); },
	get cartBubbles() { return document.querySelectorAll(CART_CONFIG.selectors.cartBubbles); },
	
	cartItem: (index) => document.querySelector(CART_CONFIG.selectors.lineIndex.replace('{{index}}', index)),
	
	cartQuantity: (index) => DOM.cartItem(index)?.querySelector(CART_CONFIG.selectors.itemQuantity)
};

/*********************************************************************************************
 * 🔌 CORE CART API
 */
const CartAPI = {
	async request(method, path, body = null) {
		cartEmit('api-start', { method, path });

		const baseUrl = window.Shopify?.routes?.root || '/';
		const url = baseUrl + path;

		const isFormData = (typeof FormData !== 'undefined') && body instanceof FormData;

		const config = { method };

		// 👉 IMPORTANT : n'ajoute pas Content-Type si c'est du FormData
		if (!isFormData) {
			config.headers = { 'Content-Type': 'application/json' };
		}

		if (body && method !== 'GET') {
			config.body = isFormData ? body : JSON.stringify(body);
		}

		try {
			const response = await fetch(url, config);
			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			const data = await response.json();

			if (data.status && data.status >= 400) {
				throw new Error(data.message || data.description || 'Shopify API Error');
			}

			cartEmit('api-success', { method, path, itemCount: data.item_count });
			this.updateUI(data);
			return data;
		} catch (error) {
			cartEmit('api-error', { method, path, error: error.message });
			throw error;
		}
	},

	/**
	 * Updates cart UI elements with new data
	 */
	updateUI(data) {
		// Update cart count
		const count = data.item_count || 0;
		DOM.cartBubbles.forEach(bubble => {
			bubble.classList.toggle('mm-none', count === 0);
			bubble.textContent = count || '';
		});
		cartEmit('count-updated', { count });

		// Update cart content if sections exist
		if (data.sections && DOM.cart) {
			const sectionId = DOM.cart.id.replace('shopify-section-', '');
			if (data.sections[sectionId]) {
				this.refreshDOM(data.sections[sectionId]);
			}
		}
	},

	/**
	 * Refreshes cart DOM with new HTML and reinitializes components
	 */
	refreshDOM(html) {
		const temp = document.createElement('div');
		temp.innerHTML = html;
		
		const newCart = temp.querySelector(CART_CONFIG.selectors.drawerCart);
		const currentCart = DOM.cartSection?.querySelector(CART_CONFIG.selectors.drawerCart);
		
		if (newCart && currentCart) {
			currentCart.innerHTML = newCart.innerHTML;
			// Reinitialize Splide sliders with auto-init
			if (typeof Splide !== 'undefined') {
				document.querySelectorAll('.mm-drawer-cart .splide').forEach(function(element) {
					if (!element._splide) {
						try {
							new Splide(element).mount();
						} catch (error) {
							window.log('warn', 'Cart', 'Failed to initialize Splide:', { element, error });
						}
					}
				});
			}
			// Reinitialize event listeners for new DOM elements
			if (typeof window.initAddToCartForms === 'function') {
				window.initAddToCartForms();
			}
			if (typeof window.initCartQuantityListeners === 'function') {
				window.initCartQuantityListeners();
			}
			// Reinitialize promo code listeners
			if (typeof initPromoCode === 'function') {
				initPromoCode();
			}
			cartEmit('refreshed');
		}
	}
};

/*********************************************************************************************
 * 🛒 CART DATA MANAGEMENT
 */
const Cart = {
	// Focus trap state
	focusTrap: {
		isActive: false,
		triggerElement: null,
		firstFocusable: null,
		lastFocusable: null,
		handleKeyDown: null
	},

	/**
	 * Setup focus trap for cart drawer
	 */
	setupFocusTrap(cartWrapper, triggerElement) {
		const focusableElements = cartWrapper.querySelectorAll(
			'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
		);

		if (focusableElements.length === 0) return;

		this.focusTrap.triggerElement = triggerElement;
		this.focusTrap.firstFocusable = focusableElements[0];
		this.focusTrap.lastFocusable = focusableElements[focusableElements.length - 1];

		// Handle Tab and Escape keys
		this.focusTrap.handleKeyDown = (e) => {
			// Escape key closes cart
			if (e.key === 'Escape') {
				e.preventDefault();
				this.close();
				return;
			}

			// Tab key traps focus
			if (e.key !== 'Tab') return;

			if (e.shiftKey) {
				// Shift + Tab: going backwards
				if (document.activeElement === this.focusTrap.firstFocusable) {
					e.preventDefault();
					this.focusTrap.lastFocusable?.focus();
				}
			} else {
				// Tab: going forwards
				if (document.activeElement === this.focusTrap.lastFocusable) {
					e.preventDefault();
					this.focusTrap.firstFocusable?.focus();
				}
			}
		};

		document.addEventListener('keydown', this.focusTrap.handleKeyDown);
		this.focusTrap.isActive = true;

		// Focus first element
		setTimeout(() => this.focusTrap.firstFocusable?.focus(), 100);

		window.log('info', 'Cart', 'Focus trap activated');
	},

	/**
	 * Remove focus trap
	 */
	removeFocusTrap() {
		if (this.focusTrap.isActive && this.focusTrap.handleKeyDown) {
			document.removeEventListener('keydown', this.focusTrap.handleKeyDown);
			this.focusTrap.isActive = false;
			this.focusTrap.triggerElement = null;
			this.focusTrap.firstFocusable = null;
			this.focusTrap.lastFocusable = null;
			this.focusTrap.handleKeyDown = null;
			window.log('info', 'Cart', 'Focus trap removed');
		}
	},

	async get() {
		cartEmit('fetch-start');
		const data = await CartAPI.request('GET', 'cart.js');
		cartEmit('fetched', { itemCount: data.item_count, totalPrice: data.total_price });
		return data;
	},

	/**
	 * Open cart
	 */
	open(triggerElement) {
		if (!cartEmit('before-open', {}, true)) return;

		this.get().catch(error => window.log('error', 'Cart', 'Failed to fetch cart on open', { error: error.message }));

		// Vérifie les cadeaux à ajout automatique : si un seuil est déjà franchi
		// (panier rempli avant l'ouverture), le cadeau auto est ajouté ici, car
		// processFreeProducts n'est sinon déclenché que sur add/change de quantité.
		this.processFreeProducts().catch(error => window.log('error', 'Cart', 'Failed to process free products on open', { error: error.message }));

		const wrapper = DOM.cartWrapper;
		const html = document.documentElement;

		if (wrapper && html) {
			wrapper.classList.add('mm-dc-is-open');
			this._lockScroll();

			// Inert background content for screen readers (WCAG 2.4.3)
			document.querySelectorAll('main, header, footer').forEach(el => {
				if (!wrapper.contains(el)) el.inert = true;
			});

			// Hide overlays
			['shopify-chat', 'axeptio_overlay'].forEach(id => {
				const elem = document.getElementById(id);
				if (elem) elem.style.display = 'none';
			});

			// Fallback to active element so focus is always restored on close (WCAG 2.4.3)
			const trigger = triggerElement || document.activeElement;
			this.setupFocusTrap(wrapper, trigger);

			cartEmit('opened');
		}
	},

	/**
	 * Close cart
	 */
	close() {
		if (!cartEmit('before-close', {}, true)) return;

		const wrapper = DOM.cartWrapper;

		if (wrapper) wrapper.classList.remove('mm-dc-is-open');
		this._unlockScroll();

		// Restore background content
		document.querySelectorAll('[inert]').forEach(el => el.inert = false);

		// Remove focus trap
		const trigger = this.focusTrap.triggerElement;
		this.removeFocusTrap();
		if (trigger && wrapper?.contains(document.activeElement)) trigger.focus({ preventScroll: true });

		// Show overlays
		['shopify-chat', 'axeptio_overlay'].forEach(id => {
			const elem = document.getElementById(id);
			if (elem) elem.style.display = '';
		});

		cartEmit('closed');
	},

	/**
	 * Verrouille le scroll de la page derrière le drawer.
	 *
	 * `overflow:hidden` sur <html> ne suffit pas : iOS Safari continue de
	 * scroller le body au toucher (double-scroll). On fige donc le body en
	 * position:fixed en mémorisant la position de scroll, restaurée au close.
	 */
	_lockScroll() {
		if (this._scrollLocked) return;
		this._scrollY = window.scrollY;
		document.documentElement.classList.add('stop-scrolling');
		document.body.style.position = 'fixed';
		document.body.style.top = `-${this._scrollY}px`;
		document.body.style.width = '100%';
		this._scrollLocked = true;
	},

	/**
	 * Déverrouille le scroll et restaure la position d'origine.
	 */
	_unlockScroll() {
		if (!this._scrollLocked) return;
		document.documentElement.classList.remove('stop-scrolling');
		document.body.style.position = '';
		document.body.style.top = '';
		document.body.style.width = '';
		window.scrollTo(0, this._scrollY || 0);
		this._scrollLocked = false;
	},

	/**
	 * Cadeaux mutuellement exclusifs (remplacement auto).
	 *
	 * Avant d'ajouter un cadeau d'un groupe, retire du panier toute autre ligne
	 * cadeau (_gift) appartenant au MÊME groupe. Les variants du groupe sont lus
	 * dans le contrat [data-free-products] (data-gift-group). On ne touche qu'aux
	 * lignes marquées _gift, jamais à un produit acheté volontairement.
	 *
	 * @param {string} group - identifiant du groupe (data-gift-group du form)
	 * @param {string} keepVariantId - variant en cours d'ajout, à ne pas retirer
	 */
	async replaceGiftGroup(group, keepVariantId) {
		if (!group) return;

		// Variants déclarés dans ce groupe (hors celui qu'on ajoute).
		const container = DOM.cart?.querySelector(CART_CONFIG.selectors.freeProducts);
		if (!container) return;
		const groupVariantIds = Array.from(container.children)
			.filter(el => el.dataset.giftGroup === group)
			.map(el => el.dataset.variantId)
			.filter(id => id && String(id) !== String(keepVariantId));
		if (!groupVariantIds.length) return;

		// Lignes cadeau réellement au panier pour ces variants.
		const cart = await CartAPI.request('GET', 'cart.js');
		const linesToRemove = cart.items.filter(it =>
			it.properties?._gift === 'true' && groupVariantIds.includes(String(it.variant_id))
		);
		if (!linesToRemove.length) return;

		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
		for (const line of linesToRemove) {
			cartEmit('free-product-removing', { variantId: line.variant_id });
			await CartAPI.request('POST', 'cart/change.js', {
				id: line.key,
				quantity: 0,
				...(sectionId ? { sections: sectionId } : {})
			});
		}
	},

	/**
	 * Get quantity
	 */
	getQuantity(lineIndex) {
		const elem = DOM.cartQuantity(lineIndex);
		return elem ? parseInt(elem.dataset.itemQuantity, 10) || 0 : 0;
	},

	/**
	 * Extract comprehensive item data from cart DOM
	 */
	getItemData(lineIndex) {
		const itemEl = DOM.cartItem(lineIndex);
		if (!itemEl) return null;

		const data = {
			lineIndex: parseInt(lineIndex, 10),
			variantId: itemEl.dataset.cartItemId,
			quantity: this.getQuantity(lineIndex),
			maxQuantity: itemEl.dataset.maxQuantity,
			properties: {},
			variants: {},
			metadata: {}
		};

		// Extract properties from DOM elements (if displayed)
		const propertyElements = itemEl.querySelectorAll('[data-property]');
		propertyElements.forEach(el => {
			const propName = el.dataset.property;
			const propValue = el.textContent.trim();
			if (propName && propValue) {
				data.properties[propName] = propValue;
			}
		});

		// Extract variant options (if displayed)
		const variantElements = itemEl.querySelectorAll('[data-variant-option]');
		variantElements.forEach(el => {
			const optionName = el.dataset.variantOption;
			const optionValue = el.textContent.trim();
			if (optionName && optionValue) {
				data.variants[optionName] = optionValue;
			}
		});

		// Add any other metadata from data attributes
		Object.keys(itemEl.dataset).forEach(key => {
			if (!['cartItemId', 'lineIndex', 'maxQuantity'].includes(key)) {
				data.metadata[key] = itemEl.dataset[key];
			}
		});

		return data;
	},

	/**
	 * Set quantity
	 */
	setQuantity(lineIndex, newQty, skipEvent = false) {
		const elem = DOM.cartQuantity(lineIndex);
		if (!elem || newQty < 0) return;
		
		const oldQty = parseInt(elem.dataset.itemQuantity, 10) || 0;
		elem.dataset.itemQuantity = newQty;
		elem.textContent = newQty;
		
		if (!skipEvent) {
			cartEmit('quantity-changed', { lineIndex, oldQty, newQty });
		}
	},

	/**
	 * Update quantity
	 */
	// Verrou de sérialisation : les opérations panier (change + auto-add du
	// cadeau qui suit) doivent s'enchaîner, jamais se chevaucher. Sans ça, un
	// clic rapide pendant le double-refresh de l'auto-add crée des courses
	// (quantité périmée, lignes dupliquées).
	_syncChain: Promise.resolve(),

	updateQuantity(lineIndex, newQty) {
		this._syncChain = this._syncChain
			.catch(() => {})
			.then(() => this._doUpdateQuantity(lineIndex, newQty));
		return this._syncChain;
	},

	async _doUpdateQuantity(lineIndex, newQty) {
		const itemEl = DOM.cartItem(lineIndex);
		const variantId = itemEl?.dataset.cartItemId;
		const lineKey = itemEl?.dataset.lineKey;

		cartEmit('quantity-sync-start', { lineIndex, quantity: newQty, variantId });

		try {
			const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

			// On cible la ligne par sa `key` (identifiant unique de ligne) en
			// priorité, sinon par `id` (variant). Deux raisons :
			// 1. l'ajout automatique d'un cadeau réordonne le panier → les index
			//    `line` se décalent ;
			// 2. un même produit peut exister sur deux lignes (achat + cadeau _gift)
			//    → cibler par variant serait ambigu, la `key` les distingue.
			const requestBody = lineKey
				? { id: lineKey, quantity: newQty }
				: variantId
					? { id: variantId, quantity: newQty }
					: { line: parseInt(lineIndex, 10), quantity: newQty };

			// Add sections for bundled section rendering if available
			if (sectionId) {
				requestBody.sections = sectionId;
			}
			
			const data = await CartAPI.request('POST', 'cart/change.js', requestBody);
			
			await this.processFreeProducts(data);
			cartEmit('quantity-synced', { lineIndex, quantity: newQty, variantId });
			return data;
		} catch (error) {
			cartEmit('quantity-sync-error', { lineIndex, error: error.message });
			// Fallback: refresh cart to sync with server state
			this.get().catch(error => window.log('error', 'Cart', 'Failed to fetch cart on open', { error: error.message }));
			throw error;
		}
	},

	/**
	 * Process free products
	 *
	 * Lit le contrat [data-free-products] re-rendu côté Liquid et applique les
	 * ajouts/retraits automatiques de cadeaux selon le seuil franchi.
	 *
	 * `data` est optionnel : s'il ne contient pas la section re-rendue (ex. appel
	 * à l'ouverture du drawer), on récupère la section à jour via la Section
	 * Rendering API. Ainsi l'auto-add fonctionne aussi quand le seuil a été
	 * franchi lors d'une session précédente (panier déjà rempli à l'ouverture).
	 */
	async processFreeProducts(data) {
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';
		if (!sectionId) return data;

		let sectionHtml = data?.sections?.[sectionId];
		if (!sectionHtml) {
			// Pas de section dans la réponse : on la récupère fraîche via la Section
			// Rendering API. La section MM-Cart vit dans un section group, donc on
			// passe par l'URL de page (`/?sections=`) — `cart.js?sections=` ne sait
			// pas re-rendre les sections de groupe.
			const fresh = await fetch(`${window.Shopify.routes.root}?sections=${sectionId}`).then(r => r.json());
			sectionHtml = fresh[sectionId];
		}
		if (!sectionHtml) return data;

		const temp = document.createElement('div');
		temp.innerHTML = sectionHtml;
		const freeProductsEl = temp.querySelector(CART_CONFIG.selectors.freeProducts);

		if (!freeProductsEl?.children.length) return data;

		// État réel du panier : on identifie les lignes cadeau par la propriété de
		// ligne `_gift` (et non par le variant), pour ne jamais toucher à un même
		// produit acheté volontairement par le client (ex. via le cross-sell).
		const cart = data?.item_count !== undefined ? data : await CartAPI.request('GET', 'cart.js');
		const giftLineFor = (variantId) => cart.items.find(it =>
			String(it.variant_id) === String(variantId) && it.properties && it.properties._gift === 'true'
		);

		let didChange = false;
		for (const el of Array.from(freeProductsEl.children)) {
			const variantId = el.dataset.variantId;
			const shouldHave = el.dataset.freeProduct === 'true';
			const autoAdd = el.dataset.autoAdd === 'true';
			const giftLine = giftLineFor(variantId);

			if (giftLine && !shouldHave) {
				// Retrait auto : la ligne CADEAU (marquée _gift) existe mais le seuil
				// n'est plus atteint → on retire uniquement cette ligne, par sa key.
				cartEmit('free-product-removing', { variantId });
				await CartAPI.request('POST', 'cart/change.js', {
					id: giftLine.key,
					quantity: 0,
					...(sectionId ? { sections: sectionId } : {})
				});
				didChange = true;
			} else if (!giftLine && shouldHave && autoAdd && !DeclinedGifts.has(variantId)) {
				// Ajout auto : seuil atteint, cadeau pas encore présent comme ligne
				// _gift, ajout auto activé, et le client ne l'a pas refusé. On l'ajoute
				// comme ligne distincte marquée _gift (coexiste avec un achat volontaire).
				cartEmit('free-product-adding', { variantId });
				await CartAPI.request('POST', 'cart/add.js', {
					id: Number(variantId),
					quantity: 1,
					properties: { _gift: 'true' },
					...(sectionId ? { sections: sectionId } : {})
				});
				didChange = true;
			}
		}

		if (didChange) cartEmit('free-products-updated', {});

		return data;
	}
};

/*********************************************************************************************
 * 🔢 QUANTITY CONTROLS
 */
const debouncedUpdate = window.debounce((lineIndex) => {
	const quantity = Cart.getQuantity(lineIndex);
	Cart.updateQuantity(lineIndex, quantity);
}, CART_CONFIG.debounceDelay);

/*********************************************************************************************
 * 🛏️ EXPORTED FUNCTIONS
 */

/**
 * Increases product quantity by 1
 */
window.mmIncreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const maxValue = itemData?.maxQuantity;
	const max = (maxValue && !isNaN(parseInt(maxValue, 10))) ? parseInt(maxValue, 10) : Infinity;
	const newQty = current + 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		max,
		action: 'increase'
	};

	if (!cartEmit('before-quantity-increase', eventData, true)) return;
	
	if (newQty > max) {
		cartEmit('quantity-limit-reached', eventData);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit('quantity-increased', { ...eventData, newQty });
	debouncedUpdate(lineIndex);
};

/**
 * Decreases product quantity by 1, removes if quantity would be 0
 */
window.mmDecreaseProductQuantity = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);
	const newQty = current - 1;

	const eventData = {
		...itemData,
		oldQty: current,
		newQty,
		action: 'decrease'
	};

	if (!cartEmit('before-quantity-decrease', eventData, true)) return;
	
	if (newQty < 1) {
		// If quantity would be less than 1, remove the item completely
		const removeEventData = {
			...itemData,
			oldQty: current,
			action: 'remove'
		};
		
		if (!cartEmit('before-item-remove', removeEventData, true)) return;
		
		Cart.setQuantity(lineIndex, 0);
		cartEmit('item-removed', removeEventData);
		// Suppression : synchronisation immédiate (cf. mmDestroyProductFromCart).
		Cart.updateQuantity(lineIndex, 0);
		return;
	}

	Cart.setQuantity(lineIndex, newQty);
	cartEmit('quantity-decreased', eventData);
	debouncedUpdate(lineIndex);
};

/**
 * Removes product completely from cart
 */
window.mmDestroyProductFromCart = (lineIndex) => {
	const itemData = Cart.getItemData(lineIndex);
	const current = Cart.getQuantity(lineIndex);

	const eventData = {
		...itemData,
		oldQty: current,
		action: 'destroy'
	};

	if (!cartEmit('before-item-remove', eventData, true)) return;

	// Si on supprime une ligne CADEAU (auto), mémoriser le refus : le cadeau ne
	// sera pas ré-ajouté automatiquement et repassera en teaser « Ajouter ».
	const itemEl = DOM.cartItem(lineIndex);
	if (itemEl?.dataset.lineGift === 'true' && itemEl.dataset.cartItemId) {
		DeclinedGifts.add(itemEl.dataset.cartItemId);
	}

	Cart.setQuantity(lineIndex, 0);
	cartEmit('item-removed', eventData);
	// Suppression : pas de debounce, on synchronise immédiatement (le debounce ne
	// sert qu'à grouper les clics +/- rapides, inutile pour un retrait franc).
	Cart.updateQuantity(lineIndex, 0);
};

/*********************************************************************************************
 * 🏗️ GLOBAL FUNCTIONS
 */
window.mmOpenCart = (triggerElement) => Cart.open(triggerElement);
window.mmCloseCart = () => Cart.close();
window.mmGetCart = () => Cart.get();
window.mmGetCartItemData = (lineIndex) => Cart.getItemData(lineIndex);

/*********************************************************************************************
 * 🏗️ UTILITY FUNCTIONS (MERGED ABOVE)
 */
window.updateCartCount = (value) => CartAPI.updateUI({ item_count: value });

// Cart UI Event Listeners
const initCartEventListeners = () => {
	// Close cart functionality
	document.addEventListener('click', (e) => {
		if (e.target.closest('[data-cart-close]')) {
			e.preventDefault();
			Cart.close();
		}

		// Bandeau newsletter : ouvre le popup Klaviyo natif
		const klaviyoTrigger = e.target.closest('[data-klaviyo-form]');
		if (klaviyoTrigger) {
			e.preventDefault();
			const formId = klaviyoTrigger.dataset.klaviyoForm;
			window._klOnsite = window._klOnsite || [];
			window._klOnsite.push(['openForm', formId]);
			window.log('info', '🛒 [CART]', 'Klaviyo form opened', { formId });
		}

		// Checkout button : flush note de commande avant redirection
		const checkoutBtn = e.target.closest('[data-checkout-btn]');
		if (checkoutBtn) {
			const noteField = document.querySelector(CART_CONFIG.selectors.cartNote);
			const href = checkoutBtn.getAttribute('href');
			// Si une note est saisie, on la persiste (cart/update.js) avant de partir au checkout
			if (noteField && href) {
				e.preventDefault();
				checkoutBtn.classList.add('mm-loading');
				cartEmit('checkout-loading', { element: checkoutBtn });
				CartAPI.request('POST', 'cart/update.js', { note: buildCartNote() })
					.catch(error => window.log('error', 'Cart', 'Failed to save note before checkout', { error: error.message }))
					.finally(() => { window.location.href = href; });
			} else {
				checkoutBtn.classList.add('mm-loading');
				cartEmit('checkout-loading', { element: checkoutBtn });
			}
		}

		// Discount removal
		const discountButton = e.target.closest('[data-discount-remove]');
		if (discountButton) {
			e.preventDefault();
			const discountTitle = discountButton.getAttribute('data-discount-title');
			if (discountTitle) {
				window.removeDiscount(discountTitle);
			}
		}
	});
};

/*********************************************************************************************
 * 🛒 Add to Cart Forms Handler
 */
window.initAddToCartForms = () => {
	document.querySelectorAll('[data-type="add-to-cart-form"]:not([data-cart-handler-attached])').forEach(form => {
		form.setAttribute('data-cart-handler-attached', 'true');
		form.addEventListener('submit', async (e) => {
			e.preventDefault();

			const submitButton = e.submitter || form.querySelector('[type="submit"]');
			const isBuyNow = submitButton?.hasAttribute('data-buy-now');
			const formData = new FormData(form);

			if (!formData.get('id')) {
				window.log('error', 'Cart', 'No variant ID found in form', { form });
				return;
			}

			// Add loading state
			if (submitButton) {
				submitButton.classList.add('mm-loading');
				submitButton.disabled = true;
			}

			// Extract all form data including properties and options
			const extractedData = {
				variantId: formData.get('id'),
				quantity: parseInt(formData.get('quantity') || 1, 10),
				properties: {},
				options: {},
				formEntries: {}
			};

			// Collect all form entries
			for (const [key, value] of formData.entries()) {
				extractedData.formEntries[key] = value;

				// Extract properties (custom fields)
				if (key.startsWith('properties[')) {
					const propName = key.match(/properties\[(.+)\]/)?.[1];
					if (propName && value) {
						extractedData.properties[propName] = value;
					}
				}

				// Extract options (variant selections like size, color)
				if (key.startsWith('option')) {
					extractedData.options[key] = value;
				}
			}

			// Ré-ajout volontaire d'un cadeau (form marqué _gift) → on lève un
			// éventuel refus précédent, pour que la logique auto redevienne normale.
			if (extractedData.properties._gift === 'true') {
				DeclinedGifts.remove(extractedData.variantId);
			}

			cartEmit('add-to-cart-start', extractedData);

			try {
				const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

				// Cadeaux exclusifs : si on ajoute un cadeau appartenant à un groupe,
				// retirer d'abord toute autre ligne cadeau (_gift) du même groupe déjà
				// présente, pour qu'un seul cadeau du groupe coexiste (remplacement auto).
				if (extractedData.properties._gift === 'true') {
					await Cart.replaceGiftGroup(form.dataset.giftGroup, extractedData.variantId);
				}

				// Add sections for cart update if available
				if (sectionId) {
					formData.append('sections', sectionId);
				}

				const response = await CartAPI.request('POST', 'cart/add.js', formData);
				cartEmit('add-to-cart-success', {
					...extractedData,
					response: response
				});

				// Process free products after adding to cart
				await Cart.processFreeProducts(response);

				if (isBuyNow) {
					window.location.href = '/checkout';
				} else {
					Cart.open();
				}

			} catch (error) {
				cartEmit('add-to-cart-error', {
					...extractedData,
					error: error.message
				});
				window.log('error', 'Cart', 'Add to cart failed', {
					error: error.message,
					...extractedData
				});
			} finally {
				// Remove loading state
				if (submitButton) {
					submitButton.classList.remove('mm-loading');
					submitButton.disabled = false;
				}
			}
		});
	});
};

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Quantity Selector Event Listeners (replace inline onclick)
 */
window.initCartQuantityListeners = function initCartQuantityListeners() {
	// Cart decrease buttons
	document.querySelectorAll('[data-cart-decrease]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartDecrease;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmDecreaseProductQuantity(lineIndex);
			if (priceContainer) window.LoaderManager.loadThis(priceContainer);
		});
	});

	// Cart increase buttons
	document.querySelectorAll('[data-cart-increase]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartIncrease;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmIncreaseProductQuantity(lineIndex);
			if (priceContainer) window.LoaderManager.loadThis(priceContainer);
		});
	});

	// Cart remove buttons (delete item)
	document.querySelectorAll('[data-cart-remove]').forEach((btn) => {
		btn.addEventListener('click', () => {
			const lineIndex = btn.dataset.cartRemove;
			const variantId = btn.dataset.variantId;
			const priceContainer = document.querySelector(`.mm-dc-product-price-container-${variantId}`);

			window.mmDestroyProductFromCart(lineIndex);
			if (priceContainer) {
				window.LoaderManager.loadThis(priceContainer);
			} else {
				// For free products, load the button itself
				window.LoaderManager.loadThis(btn);
			}
		});
	});
}

/*********************************************************************************************
 * 💰 PROMO CODE MANAGEMENT
 */

// Un code promo restreint a des produits/segments s'applique en line_level_discount_allocations
// (par ligne), pas en cart_level_discount_applications (cart entier) — il faut checker les deux.
function getAppliedDiscountCodes(cart) {
	const codes = [];

	(cart.cart_level_discount_applications || []).forEach((d) => {
		if (d.title) codes.push(d.title);
	});

	(cart.items || []).forEach((item) => {
		(item.line_level_discount_allocations || []).forEach((alloc) => {
			const d = alloc.discount_application;
			if (d && d.type === 'discount_code' && d.title && !codes.some((c) => c.toLowerCase() === d.title.toLowerCase())) {
				codes.push(d.title);
			}
		});
	});

	return codes;
}

function initPromoCode() {
	const applyBtn = document.querySelector('[data-action="apply-discount"]');
	const discountInput = document.querySelector('input[data-name="discount"]');

	window.log('info', 'Cart', 'Initializing promo code', { applyBtn: !!applyBtn, discountInput: !!discountInput });

	if (applyBtn && discountInput && !applyBtn.dataset.promoHandlerAttached) {
		applyBtn.dataset.promoHandlerAttached = 'true';
		applyBtn.addEventListener('click', (e) => {
			e.preventDefault();
			e.stopPropagation();
			window.log('info', 'Cart', 'Apply discount button clicked');
			applyDiscount();
		});
		discountInput.addEventListener('keypress', (e) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				e.stopPropagation();
				window.log('info', 'Cart', 'Enter key pressed in discount input');
				applyDiscount();
			}
		});
		// Hide error when user starts typing
		discountInput.addEventListener('input', () => {
			hidePromoError();
		});
		window.log('info', 'Cart', 'Promo code listeners attached successfully');
	}
}

async function applyDiscount() {
	window.log('info', 'Cart', 'applyDiscount function called');

	const input = document.querySelector('input[data-name="discount"]');
	const button = document.querySelector('[data-action="apply-discount"]');

	if (!input || !button) {
		window.log('error', 'Cart', 'Input or button not found', { input: !!input, button: !!button });
		return;
	}

	const code = input.value.trim();
	window.log('info', 'Cart', 'Discount code entered', { code });

	if (!code) {
		window.log('warn', 'Cart', 'Empty discount code');
		return;
	}

	// Disable button and show loading state
	button.disabled = true;
	button.classList.add('mm-loading');

	try {
		// Get cart state before applying discount
		window.log('info', 'Cart', 'Getting cart state before discount...');
		const cartBefore = await CartAPI.request('GET', 'cart.js');
		const discountsBefore = getAppliedDiscountCodes(cartBefore);
		window.log('info', 'Cart', 'Cart before', {
			total: cartBefore.total_price,
			discounts: discountsBefore.length,
			discountsList: discountsBefore
		});

		// Check if this exact code is already applied
		const codeAlreadyApplied = discountsBefore.some(title => title.toLowerCase() === code.toLowerCase());

		if (codeAlreadyApplied) {
			showPromoError(input.dataset.errorAlreadyApplied || 'Code already applied');
			window.log('info', 'Cart', 'This code is already applied', { code });
			input.value = ''; // Clear invalid code
			return;
		}

		// Check if another discount is already applied (Shopify allows only one)
		if (discountsBefore.length > 0) {
			const existingDiscount = discountsBefore[0];
			showPromoError(`${input.dataset.errorAlreadyApplied || 'Code already applied'} (${existingDiscount})`);
			window.log('info', 'Cart', 'Another discount already applied', { existing: existingDiscount, attempted: code });
			input.value = ''; // Clear invalid code
			return;
		}

		// Check if cart is empty
		if (cartBefore.item_count === 0) {
			showPromoError(input.dataset.errorEmptyCart || 'Cart is empty');
			window.log('info', 'Cart', 'Cart is empty');
			input.value = ''; // Clear invalid code
			return;
		}

		// Apply discount via cart/update.js API
		window.log('info', 'Cart', 'Applying discount via API...', { code });

		// Get section ID for cart refresh
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		const updatePayload = {
			discount: code
		};

		if (sectionId) {
			updatePayload.sections = sectionId;
		}

		const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updatePayload)
		});

		if (!response.ok) {
			throw new Error('Cart update failed');
		}

		const updateData = await response.json();
		window.log('info', 'Cart', 'Cart updated', { updateData });

		// Verify if discount was applied (cart-level OU line-level pour codes restreints produits/segments)
		const cartAfter = await CartAPI.request('GET', 'cart.js');
		const codeApplied = getAppliedDiscountCodes(cartAfter).some(title => title.toLowerCase() === code.toLowerCase());

		if (!codeApplied) {
			// Discount was not applied - code is invalid
			window.log('warn', 'Cart', 'Discount was not applied by Shopify', { code });

			// IMPORTANT: Force removal of invalid code from cart
			const cleanupPayload = {
				discount: '' // Empty string removes the discount
			};

			if (sectionId) {
				cleanupPayload.sections = sectionId;
			}

			try {
				await fetch(window.Shopify.routes.root + 'cart/update.js', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(cleanupPayload)
				});
				window.log('info', 'Cart', 'Invalid code cleaned from cart', { code });
			} catch (cleanupError) {
				window.log('error', 'Cart', 'Failed to clean invalid code', { error: cleanupError.message });
			}

			showPromoError(input.dataset.errorNotApplicable || 'Discount code not applicable');
			input.value = ''; // Clear invalid code
			return;
		}

		// Discount applied successfully, refresh UI
		window.log('info', 'Cart', 'Discount applied successfully');

		if (updateData.sections && updateData.sections[sectionId]) {
			CartAPI.refreshDOM(updateData.sections[sectionId]);
		} else {
			// Fallback: manual refresh
			if (DOM.cart) {
				const cartData = await CartAPI.request('GET', `cart.js?sections=${sectionId}`);
				if (cartData.sections && cartData.sections[sectionId]) {
					CartAPI.refreshDOM(cartData.sections[sectionId]);
				}
			}
		}

		// Clear input
		input.value = '';

		// Emit success event
		cartEmit('discount-applied', { code });
		window.log('info', 'Cart', 'Discount applied and cart refreshed');

	} catch (error) {
		window.log('error', 'Cart', 'Failed to apply discount', { error: error.message, stack: error.stack });
		showPromoError(input.dataset.errorText || 'Error applying discount code');
		input.value = ''; // Clear invalid code on error
	} finally {
		// Reset button state
		button.disabled = false;
		button.classList.remove('mm-loading');
	}
}

function showPromoError(message) {
	const errorElement = document.querySelector('[data-promo-error]');
	const input = document.querySelector('input[data-name="discount"]');

	if (errorElement) {
		errorElement.textContent = message;
		errorElement.classList.remove('mm-none');
	}

	if (input) {
		input.classList.add('error');
	}

	// Auto-hide after 5 seconds
	setTimeout(() => {
		hidePromoError();
	}, 5000);
}

function hidePromoError() {
	const errorElement = document.querySelector('[data-promo-error]');
	const input = document.querySelector('input[data-name="discount"]');

	if (errorElement) {
		errorElement.classList.add('mm-none');
		errorElement.textContent = '';
	}

	if (input) {
		input.classList.remove('error');
	}
}

window.removeDiscount = async function(code) {
	window.log('info', 'Cart', 'Removing discount', { code });

	const discountButton = document.querySelector(`[data-discount-code="${code.toLowerCase()}"] [data-discount-remove]`);

	if (discountButton) {
		// Add loading state
		discountButton.disabled = true;
		discountButton.style.opacity = '0.5';
	}

	try {
		// Get section ID for cart refresh
		const sectionId = DOM.cart?.id.replace('shopify-section-', '') || '';

		// Remove discount via cart/update.js API with empty discount parameter
		window.log('info', 'Cart', 'Removing discount via API...', { code });

		const updatePayload = {
			discount: ''  // Empty string removes the discount
		};

		if (sectionId) {
			updatePayload.sections = sectionId;
		}

		const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updatePayload)
		});

		if (!response.ok) {
			throw new Error('Cart update failed');
		}

		const updateData = await response.json();
		window.log('info', 'Cart', 'Discount removal response', { updateData });

		// Verify discount was removed (cart-level OU line-level pour codes restreints produits/segments)
		const cartAfter = await CartAPI.request('GET', 'cart.js');
		const discountStillPresent = getAppliedDiscountCodes(cartAfter).some(title => title.toLowerCase() === code.toLowerCase());

		if (discountStillPresent) {
			window.log('error', 'Cart', 'Discount was not removed', { code });
			throw new Error('Discount removal failed');
		}

		window.log('info', 'Cart', 'Discount removed successfully', { code });

		// Refresh UI
		if (updateData.sections && updateData.sections[sectionId]) {
			CartAPI.refreshDOM(updateData.sections[sectionId]);
		} else {
			// Fallback: manual refresh
			if (DOM.cart) {
				const cartData = await CartAPI.request('GET', `cart.js?sections=${sectionId}`);
				if (cartData.sections && cartData.sections[sectionId]) {
					CartAPI.refreshDOM(cartData.sections[sectionId]);
				}
			}
		}

		// Emit success event
		cartEmit('discount-removed', { code });
		window.log('info', 'Cart', 'Discount removed and cart refreshed');

	} catch (error) {
		window.log('error', 'Cart', 'Failed to remove discount', { error: error.message, stack: error.stack });

		// Reset button state on error
		if (discountButton) {
			discountButton.disabled = false;
			discountButton.style.opacity = '1';
		}
	}
};

/*********************************************************************************************
 * 📝 ORDER NOTE
 */
function buildCartNote() {
	return Array.from(document.querySelectorAll(CART_CONFIG.selectors.cartNote))
		.filter(field => field.value.trim())
		.map(field => `${field.dataset.noteLabel} : ${field.value.trim()}`)
		.join('\n\n');
}

const debouncedNoteUpdate = window.debounce(() => {
	const note = buildCartNote();
	CartAPI.request('POST', 'cart/update.js', { note })
		.then(() => cartEmit('note-updated', { note }))
		.catch(error => window.log('error', 'Cart', 'Failed to update note', { error: error.message }));
}, CART_CONFIG.debounceDelay);

// Delegated input handler (survives refreshDOM, no re-init needed)
document.addEventListener('input', (e) => {
	if (e.target.closest(CART_CONFIG.selectors.cartNote)) {
		debouncedNoteUpdate();
	}
});

// À l'ouverture de l'accordéon code promo, focus le champ (capture car `toggle` ne bubble pas)
document.addEventListener('toggle', (e) => {
	const details = e.target;
	if (details.classList && details.classList.contains('mm-dc-promo') && details.open) {
		const input = details.querySelector('input[data-name="discount"]');
		if (input) setTimeout(() => input.focus(), 50);
	}
}, true);

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */
const initCart = () => {
	cartEmit('initializing');
	window.initCartQuantityListeners();
	initPromoCode();
	initCartEventListeners();
	window.initAddToCartForms();

	Cart.get()
		.then(data => {
			cartEmit('initialized', { itemCount: data.item_count, totalPrice: data.total_price });
		})
		.catch(error => {
			window.log('error', 'Cart', 'Cart initialization failed', { error: error.message });
			cartEmit('initialization-error', { error: error.message });
		});
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initCart);
} else {
	initCart();
}

